import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, DatePicker, Checkbox, Button, Input, Tooltip, Tag } from "antd";
import Image from "helpers/Image";
import moment from "moment";
import { useFilterOptions } from "hooks/analytics/analytics";
import "./style.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * The one filter bar every analytics report uses.
 *
 * Built as a single shared component on purpose: the four original report
 * screens each grew their own filter handling, which is why one took a month,
 * three took a single day, and only one could filter by contractor.
 *
 * Filter state is mirrored into the URL so a view can be bookmarked or sent to
 * someone, and read back from the URL on load.
 */

const API_DATE = "YYYY-MM-DD";

/** Ranges people actually ask for, so nobody picks dates by hand for common cases. */
export const DATE_PRESETS = {
  today: { label: "Today", range: () => [moment(), moment()] },
  yesterday: {
    label: "Yesterday",
    range: () => [moment().subtract(1, "day"), moment().subtract(1, "day")],
  },
  last7: { label: "Last 7 days", range: () => [moment().subtract(6, "days"), moment()] },
  last30: { label: "Last 30 days", range: () => [moment().subtract(29, "days"), moment()] },
  thisMonth: {
    label: "This month",
    range: () => [moment().startOf("month"), moment()],
  },
  lastMonth: {
    label: "Last month",
    range: () => [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  },
  last90: { label: "Last 90 days", range: () => [moment().subtract(89, "days"), moment()] },
};

export const buildFilters = (state) => ({
  fromDate: state.dates?.[0] ? state.dates[0].format(API_DATE) : undefined,
  toDate: state.dates?.[1] ? state.dates[1].format(API_DATE) : undefined,
  compareToPrevious: !!state.compare,
  siteIds: state.siteIds?.length ? state.siteIds : undefined,
  contractorIds: state.contractorIds?.length ? state.contractorIds : undefined,
  shiftIds: state.shiftIds?.length ? state.shiftIds : undefined,
  designation: state.designation || undefined,
  employmentStatus: state.employmentStatus || "ALL",
  searchKey: state.searchKey || undefined,
});

const defaultState = () => ({
  preset: "last30",
  dates: DATE_PRESETS.last30.range(),
  compare: true,
  siteIds: [],
  contractorIds: [],
  shiftIds: [],
  designation: undefined,
  employmentStatus: "ALL",
  searchKey: "",
});

const readUrl = (params) => {
  const base = defaultState();
  const from = params.get("from");
  const to = params.get("to");
  if (from && to) {
    base.dates = [moment(from, API_DATE), moment(to, API_DATE)];
    base.preset = params.get("preset") || "custom";
  }
  if (params.get("compare") !== null) base.compare = params.get("compare") === "1";
  const ids = (key) =>
    (params.get(key) || "")
      .split(",")
      .filter(Boolean)
      .map((v) => Number(v));
  base.siteIds = ids("sites");
  base.contractorIds = ids("contractors");
  base.shiftIds = ids("shifts");
  base.designation = params.get("designation") || undefined;
  base.employmentStatus = params.get("status") || "ALL";
  base.searchKey = params.get("q") || "";
  return base;
};

/**
 * `title` and `actions` share the top row with the period controls: the screen
 * title sits at the left, the period select, range picker and export button at
 * the right. The screens therefore render no header card of their own.
 */
const FilterBar = ({
  onApply,
  showCompare = true,
  showSearch = true,
  extra = null,
  actions = null,
  title = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(() => readUrl(searchParams));
  const { data: optionsRes, isLoading: optionsLoading } = useFilterOptions();
  const options = optionsRes?.data || {};

  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));

  /** Writes the current filters to the URL so the view is shareable. */
  const syncUrl = (next) => {
    const params = {};
    if (next.dates?.[0] && next.dates?.[1]) {
      params.from = next.dates[0].format(API_DATE);
      params.to = next.dates[1].format(API_DATE);
      params.preset = next.preset;
    }
    params.compare = next.compare ? "1" : "0";
    if (next.siteIds?.length) params.sites = next.siteIds.join(",");
    if (next.contractorIds?.length) params.contractors = next.contractorIds.join(",");
    if (next.shiftIds?.length) params.shifts = next.shiftIds.join(",");
    if (next.designation) params.designation = next.designation;
    if (next.employmentStatus && next.employmentStatus !== "ALL")
      params.status = next.employmentStatus;
    if (next.searchKey) params.q = next.searchKey;
    setSearchParams(params, { replace: true });
  };

  const apply = (override) => {
    const next = { ...state, ...(override || {}) };
    syncUrl(next);
    onApply(buildFilters(next), next);
  };

  // Run once on mount so a bookmarked URL loads its own view immediately.
  useEffect(() => {
    onApply(buildFilters(state), state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayCount = useMemo(() => {
    if (!state.dates?.[0] || !state.dates?.[1]) return 0;
    return state.dates[1].diff(state.dates[0], "days") + 1;
  }, [state.dates]);

  const activeCount = useMemo(
    () =>
      (state.siteIds?.length ? 1 : 0) +
      (state.contractorIds?.length ? 1 : 0) +
      (state.shiftIds?.length ? 1 : 0) +
      (state.designation ? 1 : 0) +
      (state.employmentStatus !== "ALL" ? 1 : 0) +
      (state.searchKey ? 1 : 0),
    [state]
  );

  const reset = () => {
    const fresh = defaultState();
    setState(fresh);
    syncUrl(fresh);
    onApply(buildFilters(fresh), fresh);
  };

  return (
    <div className="fb">
      <div className="fb-row fb-row-primary">
        {title && <div className="fb-title title">{title}</div>}

        <Select
          className="fb-period"
          value={state.preset}
          onChange={(preset) => {
            // "custom" is what the range picker sets; picking it here just waits
            // for the dates rather than jumping the range somewhere arbitrary.
            if (preset === "custom") {
              set({ preset });
              return;
            }
            const dates = DATE_PRESETS[preset].range();
            set({ preset, dates });
            apply({ preset, dates });
          }}
        >
          {Object.entries(DATE_PRESETS).map(([key, preset]) => (
            <Option key={key} value={key}>
              {preset.label}
            </Option>
          ))}
          <Option value="custom">Custom range</Option>
        </Select>

        <RangePicker
          className="fb-range"
          allowClear={false}
          value={state.dates}
          format="DD/MM/YYYY"
          disabledDate={(current) => current && current > moment().endOf("day")}
          onChange={(dates) => {
            set({ dates, preset: "custom" });
            apply({ dates, preset: "custom" });
          }}
        />

        {dayCount > 0 && <span className="fb-hint">{dayCount} days</span>}

        {showCompare && (
          <div className="fb-compare">
            <Checkbox
              checked={state.compare}
              onChange={(e) => {
                set({ compare: e.target.checked });
                apply({ compare: e.target.checked });
              }}
            >
              Compare with previous period
            </Checkbox>
            {state.compare && dayCount > 0 && (
              <span className="fb-hint">vs the {dayCount} days before</span>
            )}
          </div>
        )}

        {actions && <div className="fb-primary-actions">{actions}</div>}
      </div>

      <div className="fb-row">
        <Select
          className="fb-field"
          mode="multiple"
          showArrow
          allowClear
          loading={optionsLoading}
          placeholder="All sites"
          value={state.siteIds}
          maxTagCount="responsive"
          onChange={(siteIds) => set({ siteIds })}
          filterOption={(input, option) =>
            String(option?.children?.[0] ?? option?.children ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {(options.sites || []).map((site) => (
            <Option key={site.id} value={site.id}>
              {site.name}
              {!site.hasData && <Tag className="fb-tag">no data</Tag>}
            </Option>
          ))}
        </Select>

        {/* The field labels are gone, so the notes they carried move onto the
            control itself rather than being dropped. */}
        <Tooltip title="The contractor company supplying the worker (stored as department).">
          <Select
            className="fb-field"
            mode="multiple"
            showArrow
            allowClear
            loading={optionsLoading}
            placeholder="All contractors"
            value={state.contractorIds}
            maxTagCount="responsive"
            onChange={(contractorIds) => set({ contractorIds })}
            filterOption={(input, option) =>
              String(option?.children?.[0] ?? option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {(options.contractors || []).map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
                {!c.hasData && <Tag className="fb-tag">no data</Tag>}
              </Option>
            ))}
          </Select>
        </Tooltip>

        <Select
          className="fb-field"
          allowClear
          showSearch
          loading={optionsLoading}
          placeholder="All designations"
          value={state.designation}
          onChange={(designation) => set({ designation })}
          filterOption={(input, option) =>
            String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
          }
        >
          {(options.designations || []).map((d) => (
            <Option key={d} value={d}>
              {d}
            </Option>
          ))}
        </Select>

        <Tooltip title="Only employees with an assigned shift are matched.">
          <Select
            className="fb-field"
            mode="multiple"
            showArrow
            allowClear
            loading={optionsLoading}
            placeholder="All shifts"
            value={state.shiftIds}
            maxTagCount="responsive"
            onChange={(shiftIds) => set({ shiftIds })}
          >
            {(options.shifts || []).map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
                {!s.assignedEmployees && <Tag className="fb-tag">unassigned</Tag>}
              </Option>
            ))}
          </Select>
        </Tooltip>

        <Select
          className="fb-field"
          value={state.employmentStatus}
          onChange={(employmentStatus) => set({ employmentStatus })}
        >
          {(options.employmentStatuses || ["ALL", "ACTIVE", "INACTIVE"]).map((s) => (
            <Option key={s} value={s}>
              {s === "ALL" ? "All employees" : s === "ACTIVE" ? "Active only" : "Inactive only"}
            </Option>
          ))}
        </Select>

        {showSearch && (
          <Input
            className="fb-field fb-search"
            allowClear
            prefix={<Image className="search-img" name={"search.svg"} />}
            placeholder="Name, card ID or contractor"
            value={state.searchKey}
            onChange={(e) => set({ searchKey: e.target.value })}
            onPressEnter={() => apply()}
          />
        )}

        {extra}

        <div className="fb-actions">
          <Button type="primary" onClick={() => apply()}>
            Apply
          </Button>
          <Button type="link" onClick={reset} disabled={!activeCount && state.preset === "last30"}>
            Reset{activeCount ? ` (${activeCount})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
